import { Component, OnDestroy, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { DocumentDataService } from '@shared/components/files/shared/document-data.service';
import { ModalDialogComponent } from '@shared/components/modal-dialog/modal-dialog.component';
import { DownloadService } from '@shared/components/files/shared/download.service';

type PreviewKind = 'pdf' | 'image' | 'text' | undefined;

@Component({
  selector: 'oes-files-preview',
  templateUrl: './files-preview.component.html',
  styleUrls: ['./files-preview.component.scss'],
})
export class FilesPreviewComponent implements OnDestroy {
  @ViewChild(ModalDialogComponent, { static: false })
  private modalDialog: ModalDialogComponent;

  errorMessage: string | undefined;
  fileName = '';
  isLoading = false;
  previewKind: PreviewKind;
  previewUrl: SafeResourceUrl | undefined;
  textContent: string | undefined;
  title = '';

  private objectUrl: string | undefined;
  private previewRequest?: Subscription;

  constructor(
    private _documentDataService: DocumentDataService,
    private _downloadService: DownloadService,
    private _sanitizer: DomSanitizer,
    private _translateService: TranslateService
  ) {}

  ngOnDestroy(): void {
    this.resetState();
  }

  show(targetOrganizationId: string, node: any): void {
    if (!node?.data?.documentKey?.key) {
      return;
    }

    this.resetState();
    this.fileName = node.key || node.data?.path || '';
    this.title = `${this._translateService.instant('buttons.preview')}: ${this.fileName}`;
    this.modalDialog.show();

    const fileType = this._downloadService.getFileType(node.data.path).toLowerCase();
    this.isLoading = true;

    this.previewRequest = this._documentDataService
      .downloadDocumentsAsBlob([node.data.documentKey.key], targetOrganizationId)
      .pipe(take(1))
      .subscribe({
        next: (data: Blob) => {
          this.isLoading = false;
          if (this._downloadService.pdfExtension.includes(fileType)) {
            this.openPdfInNewTab(data);
            return;
          }

          if (this._downloadService.imageExtensions.includes(fileType)) {
            this.setObjectUrl(data, 'image');
            return;
          }

          if (this._downloadService.textExtensions.includes(fileType)) {
            this.renderTextPreview(data, fileType);
            return;
          }

          this.errorMessage = this._translateService.instant('files.preview.unsupported');
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = this._translateService.instant('data-room.dialogs.download-failed');
        },
      });
  }

  onClose(): void {
    this.resetState();
  }

  private renderTextPreview(data: Blob, fileType: string): void {
    const reader = new FileReader();
    reader.onload = () => {
      const rawText = reader.result?.toString() || '';
      if (fileType === '.json') {
        try {
          this.textContent = JSON.stringify(JSON.parse(rawText), null, 2);
        } catch {
          this.textContent = rawText;
        }
      } else {
        this.textContent = rawText;
      }
      this.previewKind = 'text';
    };
    reader.onerror = () => {
      this.errorMessage = this._translateService.instant('data-room.dialogs.download-failed');
    };
    reader.readAsText(data);
  }

  private openPdfInNewTab(data: Blob): void {
    const pdfBlob = data.type === 'application/pdf' ? data : new Blob([data], { type: 'application/pdf' });
    const objectUrl = URL.createObjectURL(pdfBlob);
    window.open(objectUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    this.modalDialog.hide();
  }

  private setObjectUrl(data: Blob, previewKind: Exclude<PreviewKind, 'text' | undefined>): void {
    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(data);
    this.previewUrl = this._sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    this.previewKind = previewKind;
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = undefined;
    }
  }

  private resetState(): void {
    this.previewRequest?.unsubscribe();
    this.previewRequest = undefined;
    this.revokeObjectUrl();
    this.errorMessage = undefined;
    this.fileName = '';
    this.isLoading = false;
    this.previewKind = undefined;
    this.previewUrl = undefined;
    this.textContent = undefined;
    this.title = '';
  }
}
